import microOrgs from '@/sui/data/microOrgs'
import units from '@/sui/data/units'
import rtti from '@/sui/tools/rtti'

function checkMissingColumns (sheet) {
  const errors = []
  const checkList = [sheet]

  for (let i = 0; i < checkList.length; i++) {
    const checkValues = checkList[i]

    for (let fieldName in checkValues) {
      const fieldContent = checkValues[fieldName]
      if (rtti.isArray(fieldContent)) {
        for (let j = 0; j < fieldContent.length; j++) {
          const listElement = fieldContent[j]
          checkList.push(listElement)
        }
        continue
      }
      if (fieldContent === undefined) {
        errors.push(`Undefined column for field «${fieldName}»`)
      }
    }
  }

  if (errors.length > 0) {
    throw new Error(errors.join('<br>'))
  }
}

function createList (p, f, n, prefix) {
  const result = []
  for (let i = 1; i !== n; ++i) {
    const elt = f(`${prefix}${i}`, p)
    if (elt != null) {
      result.push(elt)
    }
  }

  return result
}

function getSeverity (score, scoreName) {
  if (score === undefined) {
    return undefined
  }

  if (score == null || score === '') {
    return null
  }

  return { code: scoreName, value: score }
}

function getOtherSeverityScoreAndValue (scoreName, scoreValue) {
  if (scoreName === undefined || scoreValue === undefined) {
    return { n: null, v: undefined }
  }

  if (scoreName === '' || scoreName === null ||
      scoreValue === '' || scoreValue === null
  ) {
    return { n: null, v: null }
  }

  return { n: scoreName, v: scoreValue }
}

function getValue (v, good) {
  if (v === undefined) {
    return undefined
  }

  if (good.indexOf(v) !== -1) {
    return v
  }
  return null
}

function nullIfEmpty (v) {
  if (v === undefined) {
    return undefined
  }

  if (v == null || v === '') {
    return null
  }

  return v
}

function newDate (date) {
  if (date === undefined) {
    return undefined
  }

  if (date === null || date === '') {
    return null
  }

  const values = date.split('/')
  const day = parseInt(values[0], 10)
  const month = parseInt(values[1], 10)
  const year = parseInt(values[2], 10)
  return new Date(year, month - 1, day, 10)
}

function createBacteremia (omb, withPreviousManip) {
  return function (prefix, p) {
    const episod = p[`${prefix}Episod`]
    if (episod == null || episod === '') {
      return null
    }
    const insertion = p[`${prefix}Insertion`]
    const result = {
      present: 1,
      episod: newDate(episod),
      diagDate: newDate(p[`${prefix}DiagDate`]),
      origin: getValue(p[`${prefix}Origin`], [1, 2, 3, 4, 5, 9]),
      covidStatus: getValue(p[`${prefix}Covid`], [1, 2, 3, 9]),
      entrance_v2: getValue(
        p[`${prefix}Entrance`],
        [
          'C8', 'C9', 'C10', 'C11', 'C12', 'C13', 'C14', 'D2', 'D3', 'D4', 'A1',
          'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12',
          'A13'
        ]
      ),
      insertion: insertion != null
        ? newDate(insertion) : newDate(p[`${prefix}CatheterDate`]),
      insertionSite: getValue(
        p[`${prefix}InsertionSite`],
        omb ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [1, 2, 3, 4, 6, 7, 8, 9, 10]
      ),
      removed: getValue(p[`${prefix}Removed`], [1, 2, 9]),
      removal: newDate(p[`${prefix}Removal`]),
      divDiag: getValue(p[`${prefix}DivDiag`], [1, 2, 3, 4, 5, 9]),
      clinicalSigns: getValue(
        p[`${prefix}ClinicalSigns`],
        [1, 2, 9]
      ),
      bioSigns: getValue(
        p[`${prefix}BioSigns`],
        [1, 2, 9]
      ),
      adaptedAntibio: getValue(
        p[`${prefix}AdaptedAntibio`],
        [1, 2, 9]
      ),
      twoHemocs: getValue(
        p[`${prefix}TwoHemocs`],
        [1, 2, 9]
      ),
      lipids: getValue(p[`${prefix}Lipids`], [1, 2, 9]),
      catheter: getValue(p[`${prefix}Catheter`], [1, 2, 9]),
      catheterType_v2: getValue(
        p[`${prefix}CatheterType`],
        [
          'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10', 'C11',
          'C12', 'C13', 'C14', 'D1'
        ]
      ),
      enfPreviousManip: getValue(p[`${prefix}EnfPreviousManip`], [1, 2, 9]),
      enfPreviousManipType: getValue(p[`${prefix}EnfPreviousManipType`], [1, 2, 3, 9]),
      enfPreviousManipDate: newDate(p[`${prefix}EnfPreviousManipDate`]),
      microOrganisms: getMicroOrganisms(p, `${prefix}`, false)
    }

    if (withPreviousManip) {
      result.previousManip = getValue(
        p[`${prefix}PreviousManip`],
        [1, 2, 9]
      )
      result.previousManipType = getValue(
        p[`${prefix}PreviousManipType`],
        [1, 2, 3, 9]
      )
      result.previousManipDate = newDate(p[`${prefix}PreviousManipDate`])
    }
    return result
  }
}

function getMicroOrganisms (o, prefix, virus) {
  let ms = []
  const list = virus ? microOrgs.completeList.v2 : microOrgs.list.v2
  for (let i = 1; i !== 5; i++) {
    let m = getMicroOrganism(o, o[`${prefix}MO${i}`], i, list, prefix)
    if (m != null) {
      ms.push(m)
    }
  }
  return ms
}

function getMicroCode (code, list) {
  const c = code.indexOf(' ') !== 3
    ? `${code.slice(0, 3)} ${code.slice(3, 6)}` : code
  const m = list.find((m) => m.code === c)
  return m != null ? m.index : null
}

function getMicroOrganism (o, code, n, list, prefix) {
  if (code == null || code.length === 0) {
    return null
  }

  const microCode = getMicroCode(code, list)
  if (microCode == null) {
    throw new Error(`Micro organisme inconnu : ${code} !`)
  }

  let rlist = []
  const resistances = o[`${prefix}Resist${n}`]
  if (resistances != null) {
    const split = resistances.split(' ')
    split.forEach(
      (s) => {
        const v = s.split('=')
        if (v.length === 2) {
          const field = v[0].toLowerCase()
          const resistance = microOrgs.resistances.v2.find(
            r => r.field === field
          )
          if (resistance != null) {
            const value = resistance.values.find(
              i => i.code === v[1] || i.alt === v[1]
            )
            if (value != null) {
              rlist.push(
                {
                  code: resistance.index,
                  resistance: value.index
                }
              )
            }
          }
        }
      }
    )
  } else {
    microOrgs.resistances.v2.forEach(
      (r) => {
        const v = o[`${prefix}${r.field}${n}`]
        if (v != null && v !== '') {
          const value = r.values.find(
            i => i.code === v || i.alt === v
          )
          if (value != null) {
            rlist.push(
              {
                code: r.index,
                resistance: value.index
              }
            )
          }
        }
      }
    )
  }

  const moDetailField = o[`${prefix}MicroOrgDetail${n}`]
  const moDetail = nullIfEmpty(moDetailField != null ? moDetailField.trim() : null)
  return { code: microCode, antibio: rlist, microOrgDetail: moDetail }
}

function getUnitTypeCode (year, unit) {
  if (unit === undefined) {
    return undefined
  }

  const list = units[year].list
  const type = list.find(u => u.code === unit)
  if (type == null) {
    return null
  }

  return type.index
}

export default {
  newDate,
  getValue,
  createBacteremia,
  getMicroOrganisms,
  getUnitTypeCode,
  createList,
  checkMissingColumns,
  nullIfEmpty,
  getSeverity,
  getOtherSeverityScoreAndValue
}
